// React i hooks
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

// React biblioteke
import { Link, useNavigate } from "react-router-dom";

// Helpers
import { classNames, getLanguageCurrentLocal, setLanguageCurrentLocal, useTranslate } from "helpers/common";

// API servisi
import { signOut } from "services/auth";
import { getActiveUser } from "services/api/users";
import { useSelector } from "react-redux";

// Notifications

// Tipovi i konstante
import { MenuProps } from "./types";

import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";

import {
  fetchLanguages,
  getLanguages,
} from "services/api/languages/redux";

const Menu: React.FC<MenuProps> = (props) => {
  const { openMenu, onOpenMenu } = props;

  const { t } = useTranslate();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const languages = useSelector(getLanguages);
  const { user } = useSelector(getActiveUser);

  const [openProfile, setOpenProfile] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dispatch(fetchLanguages() as any);
  }, [dispatch]);

  useEffect(() => {
    fetchSelectedLanguage();
  }, [languages]);

  const fetchSelectedLanguage = () => {
    if (!languages.items) return;

    var selectedItem = languages.items.find((i) => i.id === getLanguageCurrentLocal());
    if (selectedItem) {
      setLanguageCurrentLocal(selectedItem.id);
    }
  };

  const handleProfileToggle = () => {
    setOpenProfile(!openProfile);
  };

  // Close profile dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setOpenProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleAccountSettings = () => {
    setOpenProfile(false);
    navigate("/account");
  };

  const handleLogoutClicked = useCallback(() => {
    dispatch(signOut() as any);
  }, [dispatch]);

  const handleLanguageChange = useCallback(
    (e:any, name: string, item: DropdownOption) => {
      var selectedItem = languages.items.find((i) => i.id === item.value);

      if (selectedItem) {
        setLanguageCurrentLocal(selectedItem.id);
        window.location.reload();
      }
    },
    [languages]
  );

  const languageItems: DropdownOption[] = languages?.items?.map((i) => {
    return {
      value: i.id,
      text: i.name,
      image: `/media/icons/flags/${i.id}.png`,
    };
  });

  return (
    <>
      <nav id="js-menu-top" className="bg-[#161719] ">
        <div className="mx-auto">
          <div className="relative flex h-14 items-center justify-between sm:h-16">
            <div className="absolute inset-y-0 left-3 flex items-center xl:hidden">
              <button
                onClick={onOpenMenu}
                className="inline-flex items-center justify-center rounded-md p-2 hover:bg-gray-700"
              >
                <span className="sr-only">Open main menu</span>
                <div className="space-y-1">
                  <span
                    className={
                      "block w-5 h-0.5 bg-gray-300 transform transition duration-500 ease-in-out " +
                      (openMenu ? "rotate-45 translate-y-1.5" : "")
                    }
                  ></span>
                  <span
                    className={
                      "block w-5 h-0.5 bg-gray-300 transform transition duration-500 ease-in-out " +
                      (openMenu ? "opacity-0" : "")
                    }
                  ></span>
                  <span
                    className={
                      "block w-5 h-0.5 bg-gray-300 transform transition duration-500 ease-in-out " +
                      (openMenu ? "-rotate-45 -translate-y-1.5" : "")
                    }
                  ></span>
                </div>
              </button>
            </div>
            <div className={classNames("flex w-full h-full sm:flex-row ")}>
              <div
                className={classNames(
                  openMenu ? "" : "",
                  // Margina drži logo desno od gumba za meni; gumb se skriva tek na `xl`, pa se i
                  // margina smije maknuti tek ondje — inače logo završi ispod njega.
                  "ml-14 px-2 md:px-4 xl:ml-0"
                )}
              >
                <div className="flex items-center justify-center h-full ">
                  <Link
                    to="/"
                    className="flex justify-center items-center gap-4"
                  >
                    <img
                      className="h-7 w-auto block "
                      src="/media/icons/logo.svg?color=blue&shade=500"
                      alt="Your Company"
                    />
                    <span
                      className={classNames(
                        openMenu ? "block" : "hidden ",
                        "text-white"
                      )}
                    >
                      {t("admin.header.logo.title")}
                    </span>
                  </Link>
                </div>
              </div>

              <div className="absolute inset-y-0 right-0 flex gap-3 md:gap-5 items-center mr-6 ">
                {/* TODO */}
                {/* <div className="relative">
                  <Dropdown
                    name="languageId"
                    options={languageItems ?? []}
                    onChange={handleLanguageChange}
                    value={getLanguageCurrentLocal()}
                    width="small"
                    theme="dark"
                  ></Dropdown>
                </div> */}

                <div className="relative" ref={profileRef}>
                  <button
                    type="button"
                    className="flex items-center gap-2 focus:outline-none"
                    id="user-menu-button"
                    aria-expanded={openProfile}
                    aria-haspopup="true"
                    onClick={handleProfileToggle}
                  >
                    <span className="flex justify-center items-center font-bold rounded-full bg-blue-600 text-white text-[11px] w-8 h-8">
                      {user && user.firstName && user.lastName
                        ? `${user.firstName.toString().charAt(0).toUpperCase()}${user.lastName.toString().charAt(0).toUpperCase()}`
                        : ""}
                    </span>
                    <span className="hidden sm:block text-sm text-white font-medium">
                      {user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : ""}
                    </span>
                    <svg
                      className={classNames("w-4 h-4 text-slate-300 transition-transform", openProfile ? "rotate-180" : "")}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                    </svg>
                  </button>

                  {openProfile && (
                    <div
                      className="absolute right-0 z-20 mt-2 w-64 origin-top-right rounded-lg bg-[#1f2023] text-white shadow-2xl ring-1 ring-black/20 overflow-hidden"
                      role="menu"
                      aria-orientation="vertical"
                      aria-labelledby="user-menu-button"
                    >
                      <div className="px-4 py-3 border-b border-white/10">
                        <p className="text-sm font-semibold truncate">
                          {user ? `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() : ""}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{user?.email}</p>
                      </div>
                      <div className="py-1">
                        <button
                          type="button"
                          role="menuitem"
                          className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm hover:bg-white/5"
                          onClick={handleAccountSettings}
                        >
                          <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                          </svg>
                          Postavke računa
                        </button>
                      </div>
                      <div className="py-1 border-t border-white/10">
                        <button
                          type="button"
                          role="menuitem"
                          className="flex items-center gap-3 w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white/5"
                          onClick={handleLogoutClicked}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
                          </svg>
                          {t("admin.header.user.logout")}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    </>
  );
};
export default Menu;
